import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package,
  Download,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Truck,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  shipped: "bg-green-100 text-green-800 border-green-200",
  undeliverable: "bg-red-100 text-red-800 border-red-200",
  reshipped: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-gray-100 text-gray-800 border-gray-200",
};

export default function AdminDashboard() {
  const { user } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [settings, setSettings] = useState({});
  const [metrics, setMetrics] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem("adminToken");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch requests
      let requestsUrl = `${API}/admin/requests`;
      if (statusFilter) {
        requestsUrl += `?status=${statusFilter}`;
      }
      const requestsRes = await fetch(requestsUrl, { headers });
      const requestsData = await requestsRes.json();
      setRequests(requestsData);

      // Fetch counts
      const countsRes = await fetch(`${API}/admin/requests/count`, { headers });
      const countsData = await countsRes.json();
      setCounts(countsData);

      // Fetch settings
      const settingsRes = await fetch(`${API}/admin/settings`, { headers });
      const settingsData = await settingsRes.json();
      setSettings(settingsData);

      // Fetch metrics
      const metricsRes = await fetch(`${API}/admin/metrics`, { headers });
      const metricsData = await metricsRes.json();
      setMetrics(metricsData);
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusUpdate = async (requestId, newStatus) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/requests/${requestId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error("Failed to update status");

      toast.success(`Status updated to ${newStatus}`);
      setSelectedRequest(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReship = async (requestId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/requests/${requestId}/reship`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to create reship");

      toast.success("Reship created");
      setSelectedRequest(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      let url = `${API}/admin/requests/export`;
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to export");

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `pws_requests_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("CSV exported");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleIntake = async () => {
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ intake_paused: !settings.intake_paused }),
      });

      if (!res.ok) throw new Error("Failed to update settings");

      toast.success(
        settings.intake_paused ? "Intake resumed" : "Intake paused"
      );
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSaveNotes = async (notes) => {
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ inventory_notes: notes }),
      });

      if (!res.ok) throw new Error("Failed to save notes");
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--pws-plum)]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold text-[color:var(--pws-charcoal)]">
            Dashboard
          </h1>
          <p className="text-[color:var(--pws-stone)]">
            Manage underwear requests and settings
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-[color:var(--pws-line)]"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleExportCSV}
            className="bg-[color:var(--pws-plum)] hover:bg-[color:var(--pws-plum-hover)] text-white"
            data-testid="admin-export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[color:var(--pws-line)]">
          <CardContent className="p-4">
            <p className="text-sm text-[color:var(--pws-stone)]">Total Requests</p>
            <p className="text-2xl font-semibold text-[color:var(--pws-charcoal)]" data-testid="stat-total">
              {counts.total || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[color:var(--pws-line)] border-l-4 border-l-yellow-400">
          <CardContent className="p-4">
            <p className="text-sm text-[color:var(--pws-stone)]">Pending</p>
            <p className="text-2xl font-semibold text-[color:var(--pws-charcoal)]" data-testid="stat-pending">
              {counts.pending || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[color:var(--pws-line)] border-l-4 border-l-green-400">
          <CardContent className="p-4">
            <p className="text-sm text-[color:var(--pws-stone)]">Shipped</p>
            <p className="text-2xl font-semibold text-[color:var(--pws-charcoal)]" data-testid="stat-shipped">
              {counts.shipped || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[color:var(--pws-line)]">
          <CardContent className="p-4">
            <p className="text-sm text-[color:var(--pws-stone)]">Opt-in Rate</p>
            <p className="text-2xl font-semibold text-[color:var(--pws-charcoal)]" data-testid="stat-optin">
              {metrics.opt_in_rate || 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Settings & Controls */}
      <Card className="border-[color:var(--pws-line)]">
        <CardHeader>
          <CardTitle className="text-lg">Settings & Controls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pause Intake */}
            <div className="flex items-center justify-between p-4 bg-[color:var(--pws-cream)] rounded-lg">
              <div>
                <Label className="font-medium">Pause Intake</Label>
                <p className="text-sm text-[color:var(--pws-stone)]">
                  Temporarily stop accepting new requests
                </p>
              </div>
              <Switch
                checked={settings.intake_paused}
                onCheckedChange={handleToggleIntake}
                disabled={user?.role !== "admin"}
                data-testid="admin-pause-toggle"
              />
            </div>

            {/* Inventory Notes */}
            <div className="space-y-2">
              <Label>Inventory Notes</Label>
              <Textarea
                placeholder="Notes about current inventory levels..."
                defaultValue={settings.inventory_notes || ""}
                onBlur={(e) => handleSaveNotes(e.target.value)}
                className="border-[color:var(--pws-line)]"
                data-testid="admin-inventory-notes"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card className="border-[color:var(--pws-line)]">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-[color:var(--pws-plum)]" />
              Requests
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Filter:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] border-[color:var(--pws-line)]" data-testid="admin-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="undeliverable">Undeliverable</SelectItem>
                  <SelectItem value="reshipped">Reshipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="admin-requests-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Opt-in</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-[color:var(--pws-stone)]">
                      No requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((req) => (
                    <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(req.created_at)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={req.shipping_address}>
                        {req.shipping_address}
                      </TableCell>
                      <TableCell>{req.size}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[req.status]}>
                          {req.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {req.opted_in ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-400" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedRequest(req)}
                          data-testid={`view-request-${req.id}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Request Detail Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              Request ID: {selectedRequest?.id?.slice(0, 8)}...
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Size</Label>
                  <p className="font-medium">{selectedRequest.size}</p>
                </div>
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Status</Label>
                  <Badge className={statusColors[selectedRequest.status]}>
                    {selectedRequest.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Cut</Label>
                  <p className="font-medium">{selectedRequest.cut_preference || "No preference"}</p>
                </div>
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Color</Label>
                  <p className="font-medium">{selectedRequest.color_preference || "No preference"}</p>
                </div>
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Created</Label>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Opt-in</Label>
                  <p className="font-medium">{selectedRequest.opted_in ? "Yes" : "No"}</p>
                </div>
              </div>

              <div>
                <Label className="text-[color:var(--pws-stone)]">Shipping Address</Label>
                <p className="font-medium whitespace-pre-wrap bg-[color:var(--pws-cream)] p-3 rounded mt-1">
                  {selectedRequest.shipping_address}
                </p>
              </div>

              {selectedRequest.needs_text && (
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Needs/Notes</Label>
                  <p className="font-medium bg-[color:var(--pws-cream)] p-3 rounded mt-1">
                    {selectedRequest.needs_text}
                  </p>
                </div>
              )}

              {selectedRequest.opted_in && selectedRequest.contact_value && (
                <div>
                  <Label className="text-[color:var(--pws-stone)]">Contact Info</Label>
                  <p className="font-medium">
                    {selectedRequest.contact_method}: {selectedRequest.contact_value}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedRequest?.status === "pending" && (
              <>
                <Button
                  onClick={() => handleStatusUpdate(selectedRequest.id, "shipped")}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="mark-shipped-btn"
                >
                  <Truck className="h-4 w-4 mr-2" />
                  Mark Shipped
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleStatusUpdate(selectedRequest.id, "undeliverable")}
                  disabled={actionLoading}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="mark-undeliverable-btn"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Undeliverable
                </Button>
              </>
            )}
            {(selectedRequest?.status === "undeliverable" ||
              selectedRequest?.status === "shipped") && (
              <Button
                variant="outline"
                onClick={() => handleReship(selectedRequest.id)}
                disabled={actionLoading}
                className="border-[color:var(--pws-plum)] text-[color:var(--pws-plum)]"
                data-testid="reship-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Create Reship
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setSelectedRequest(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
